import React from "react";
import { Breadcrumb } from "../../../atoms/breadcrumb";
import { useTableHooks } from "../../../atoms/table/useTableHooks";
import { pascalToSnakeCase } from "../../../../utils";
import { Table } from "../../../atoms/table";
import { Input } from "../../../atoms/input";
import { PAGE_NAME, saasToolName } from "../../../../constants/constants";
import { useCostHistoryQuery, useCostReportQuery } from "../../../../store/cost/costSlice";
import { Icon } from "../../../atoms/icon";
import { MultipleDropdown } from "../../../atoms/multipleDropdown";
import { Divider } from "../../../atoms/divider";
import { usePaginationHook } from "../../../molecules/pagination/usePaginationHook";
import { Pagination } from "../../../molecules/pagination";
import { Dropdown } from "../../../atoms/dropdown";
import { DateRangePicker } from "../../../atoms/dateRange";
import { ComboChart } from "../../../molecules/comboChart";

const formatColumnName = (name: string) => {
    switch (name) {
        case "totalLicense":
            return "total_licenses";
        case "saasTotalPrice":
            return "total";
        case "createAt":
            return "created_at";
        case "month":
            return "session";
        default:
            return name;
    }
};
const CostHistory = () => {
    const pageClassName = "p-costHistory";

    const [filter, setFilter] = React.useState({
        tool_name: "Okta",
        number_of_months: 12,
    });
    const reportFilterRef = React.useRef<HTMLDivElement>(null);
    const reportRef = React.useRef<HTMLDivElement>(null);

    const [filterValue, setFilterValue] = React.useState({
        tool_name: filter.tool_name,
        number_of_months: filter.number_of_months,
    });
    const [isShowReportFilter, setShowReportFilter] = React.useState(false);

    const { data: reportData, isLoading: reportLoading, isFetching: reportFetching } = useCostReportQuery({
        tool_name: filterValue.tool_name,
        number_of_months: filterValue.number_of_months,
    });

    const reportResetFilter = () => {
        setFilter({
            tool_name: "Okta",
            number_of_months: 12,
        });
    };

    const reportFilterHandler = () => {
        setFilterValue({
            tool_name: filter.tool_name,
            number_of_months: filter.number_of_months,
        });
    };

    const options = {
        title: `Cost History Report of ${filter.tool_name} for last ${filter.number_of_months} months`,
        vAxis: { title: "Unit Price (¥)" },
        hAxis: { title: "Month" },
        seriesType: "bars",
        backgroundColor: "transparent",
        series: {
            0: { color: "blue" },
            1: { type: "line", color: "#13d443", lineWidth: 5 },
        },
    };

    const tableHeader = [
        {
            title: "Serial",
            dataIndex: "id",
            isSortable: true,
        },
        {
            title: "Saas Name",
            dataIndex: "toolName",
            isSortable: true,
        },
        {
            title: "Subscriptions Type",
            dataIndex: "subscriptionType",
            isSortable: true,
        },
        {
            title: "Month",
            dataIndex: "month",
            isSortable: true,
        },
        {
            title: "Renewal Date",
            dataIndex: "renewalDate",
            isSortable: true,
        },
        {
            title: "Total Licenses",
            dataIndex: "totalLicense",
            isSortable: true,
        },
        {
            title: "Unit Price",
            dataIndex: "unitPrice",
            isSortable: true,
        },
        {
            title: "Total Price",
            dataIndex: "saasTotalPrice",
            isSortable: true,
        },
        {
            title: "Created At",
            dataIndex: "createdAt",
            isSortable: true,
        },
    ];

    const {
        search,
        query,
        sortHandler,
        searchHandler,
        pageChangeHandler,
        limitChangeHandler,
    } = useTableHooks();

    const filterRef = React.useRef<HTMLDivElement>(null);
    const [isShowFilter, setShowFilter] = React.useState(false);

    const [queryData, setQueryData] = React.useState({
        saasTools: [] as { label: string; value: string }[],
        startDate: "",
        endDate: "",
    });

    const [saasTools, setSaasTools] = React.useState<any>([]);


    const { data, isLoading, isFetching } = useCostHistoryQuery({
        search: query.search,
        limit: query.limit,
        current_page: query.current,
        sort_by: pascalToSnakeCase(formatColumnName(query.sortBy)),
        sort_direction: query.sortDirection,
        start_date: queryData.startDate,
        end_date: queryData.endDate,
        tool_names: JSON.stringify(queryData.saasTools.map((item) => item.value)),
    });

    const {
        totalData,
        handleFirstPage,
        handlePrevPage,
        handleLastPage,
        handleNextPage,
        renderPageNumbers,
    } = usePaginationHook({
        totalNumberOfData: data?.result?.total,
        perPage: query.limit,
        setCurrentPage: pageChangeHandler,
        currentPage: query.current,
        className: "m-pagination",
    });

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                filterRef.current &&
                !filterRef.current.contains(event.target as Node)
            ) {
                setShowFilter(false);
            }

            if (
                reportFilterRef.current &&
                !reportFilterRef.current.contains(event.target as Node)
            ) {
                setShowReportFilter(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const [period, setPeriod] = React.useState<any>([]);
    const filterHandler = () => {
        setQueryData({
            saasTools: saasTools as { label: string; value: string }[],
            startDate: period[0]?.format("YYYY-M-D"),
            endDate: period[1]?.format("YYYY-M-D"),
        });
    }

    return (
        <div className={pageClassName}>
            <Breadcrumb />
            <p className={`${pageClassName}__title`}>{PAGE_NAME.COST_HISTORY}</p>
            <div className={`${pageClassName}__container`}>
                <div className={`${pageClassName}__report`}>
                    <div className={`${pageClassName}__filter`} ref={reportFilterRef}>
                        <button
                            onClick={() => setShowReportFilter(!isShowReportFilter)}
                            className={`${pageClassName}__filter__button`}
                            title="Filter"
                        >
                            <Icon icon="filter" size="primary" />
                        </button>
                        {isShowReportFilter && (
                            <div
                                className={`${pageClassName}__filter__container`}
                                ref={reportRef}
                            >
                                <div className={`${pageClassName}__filter__item`}>
                                    <label>Type</label>
                                    <Dropdown
                                        placeholder="Select Tool Name"
                                        selected={{
                                            label: filter.tool_name,
                                            value: filter.tool_name,
                                        }}
                                        options={reportData?.result?.toolNames?.map((item: any) => {
                                            const value = item.replace(/\s/g, "_");
                                            return { label: item, value: value };
                                        })}
                                        onClickChange={(e) => {
                                            setFilter({
                                                ...filter,
                                                tool_name: e.value as string,
                                            });
                                        }}
                                    />
                                </div>
                                <div className={`${pageClassName}__filter__item`}>
                                    <label htmlFor="filter">Number of Months</label>
                                    <Dropdown
                                        options={[
                                            { label: "12 Months", value: "12" },
                                            { label: "15 Months", value: "15" },
                                            { label: "18 Months", value: "18" },
                                            { label: "24 Months", value: "24" },

                                        ]}
                                        selected={{
                                            label: `${filter.number_of_months} Months`,
                                            value: filter.number_of_months,
                                        }}
                                        onClickChange={(e) =>
                                            setFilter({
                                                ...filter,
                                                number_of_months: e.value as number,
                                            })
                                        }
                                        isBordered
                                        isIcon
                                         
                                    />
                                </div>
                                <Divider />
                                <div className={`${pageClassName}__filter__btn`}>
                                    <button onClick={reportResetFilter}>Reset</button>
                                    <button
                                        onClick={() => {
                                            setShowReportFilter(false);
                                            reportFilterHandler();
                                        }}
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <ComboChart
                    data={reportData?.result?.graphData ?? []}
                    options={options}
                    isLoading={reportLoading}
                    isFetching={reportFetching}
                    style={{ width: "100%", height: "300px" }}
                />
            </div>
            <div className={`${pageClassName}__container`}>
                <div className={`${pageClassName}__header`}>
                    <div className={`${pageClassName}__search`}>
                        <Input
                            placeholder="Search"
                            onChange={searchHandler}
                            value={search}
                        />
                    </div>

                    <div className={`${pageClassName}__filter`} ref={filterRef}>
                        <button
                            onClick={() => setShowFilter(!isShowFilter)}
                            className={`${pageClassName}__filter__button`}
                            title="Filter"
                        >
                            <Icon icon="filter" size="primary" />
                        </button>
                        {isShowFilter && (
                            <div className={`${pageClassName}__filter__container`}>
                                <div className={`${pageClassName}__filter__item`}>
                                    <label>SaaS Tools</label>
                                    <MultipleDropdown
                                        value={saasTools}
                                        options={saasToolName}
                                        dropdownHandler={(value) =>
                                            setSaasTools(value as { label: string; value: string }[])
                                        }
                                        className={`${pageClassName}__filter__dropdown`}
                                    />
                                </div>

                                <div className={`${pageClassName}__filter__item`}>
                                    <label>Period</label>
                                    <DateRangePicker
                                        value={period}
                                        setValue={setPeriod}
                                        maxDate={new Date()}
                                    />
                                </div>

                                <Divider />
                                <div className={`${pageClassName}__filter__btn`}>
                                    <button
                                        onClick={() => {
                                            setSaasTools([]);
                                            setPeriod([]);
                                            handleFirstPage();
                                        }}
                                    >
                                        Reset
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowFilter(false);
                                            filterHandler();
                                            handleFirstPage();
                                        }}
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className={`${pageClassName}__table`}>
                    <Table
                        header={tableHeader}
                        data={data?.result?.data ?? []}
                        loading={isLoading || isFetching}
                        firstLoad={isLoading}
                        sortHandler={sortHandler}
                        sortBy={query.sortBy}
                        minWidth="1440"
                        startIndex={data?.result?.from}
                    />
                    {/* <p className={`${pageClassName}__note`}>
                        <span>*</span>
                        {NOTE_FOR_LICENSE_MANAGEMENT}
                    </p> */}
                </div>

                <div className={`${pageClassName}__pagination`}>
                    <div className={`${pageClassName}__pagination__limit`}>
                        <p>Display</p>
                        <div className={`${pageClassName}__pagination__dropdown`}>
                            <Dropdown
                                options={[
                                    { label: "20", value: 20 },
                                    { label: "50", value: 50 },
                                    { label: "100", value: 100 },
                                ]}
                                selected={{ label: query.limit, value: query.limit }}
                                onClickChange={(e) => {
                                    limitChangeHandler(e.value as number);
                                    handleFirstPage();
                                }}
                                isBordered
                                isIcon
                                 
                            />
                        </div>
                    </div>
                    <div className={`${pageClassName}__pagination__number`}>
                        {data?.result?.total ? (
                            <p>
                                Show {data?.result?.from} -{" "}
                                {data?.result?.from + query.limit - 1 < data?.result?.total
                                    ? data?.result?.from + query.limit - 1
                                    : data?.result?.total}{" "}
                                of {data?.result?.total}
                            </p>
                        ) : (
                            <p>Show 00 - 00 of 00</p>
                        )}
                    </div>

                    <div className={`${pageClassName}__pagination__nav`}>
                        {data?.result?.total > query.limit && (
                            <Pagination
                                totalData={totalData}
                                page={query.current}
                                handleFirstPage={handleFirstPage}
                                handlePrevPage={handlePrevPage}
                                handleLastPage={handleLastPage}
                                handleNextPage={handleNextPage}
                                renderPageNumbers={renderPageNumbers}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CostHistory;
